/*
 * kp-pdp-reviews.js — the reviews list (#18).
 *
 * Two hosts, one behaviour: the PDP section (kp-pdp-reviews) and the
 * all-reviews page (kp-all-reviews), which is the same element over a
 * single panel holding every review in the store. The tag name is the PDP's
 * because that is where it was born; the page opts into the extra control
 * it needs (the cue filter) through markup, like everything else here.
 *
 * One custom element over markup that is already complete: every review is
 * server-rendered, both lists are on the page, nothing here creates content.
 * The element adds four behaviours over the rendered cards, all local — no
 * network, no Judge.me script:
 *
 * - Tabs. A real tablist (all / product): aria-selected is the contract,
 *   Left/Right/Home/End move focus and select, one panel is visible. The
 *   markup ships both panels open for the no-script reader, so the first
 *   thing this element does is close the one whose tab is not selected and
 *   drop the headings that only that reader needs.
 * - Sort. Recent / highest / lowest / with photos, by reordering the card
 *   wrappers in the DOM from the facts they carry as data attributes.
 *   "Recent" restores the server order, which is Judge.me's own.
 * - Filter. Clicking a histogram row keeps only that rating; clicking it
 *   again clears. A panel opts in with data-kp-filterable, meaning the
 *   histogram above it counts that panel's reviews; over any other panel
 *   the rows are disabled and an active filter is dropped, because "5★ 6"
 *   clickable above a list it does not describe is a wrong number on the
 *   screen (#65). A second, optional filter — the cue select on the
 *   all-reviews page — narrows by product and stacks with the rating.
 * - Load more. The first N visible, +step per click, the button hidden when
 *   nothing is left. N and the step are per panel — the All list opens
 *   wider and loads more, because it is the one with cards to spare —
 *   falling back to the element's own numbers. Filter and sort reset the
 *   page, so the reader always sees the top of the new order.
 *
 * Plus Read more: the six-line clamp is CSS; the toggle under a quote is
 * revealed only where the quote actually overflows, measured once the fonts
 * have loaded (the clamp height depends on Rajdhani, not the fallback).
 *
 * The counts for the live region are computed from the DOM every time,
 * never cached: the visible cards are the truth and the announcement
 * repeats it.
 */
(function () {
  if (customElements.get('kp-pdp-reviews')) return;

  const NARROW = window.matchMedia('(max-width: 749px)'); /* --kp-bp-stack */
  const MID = window.matchMedia('(max-width: 989px)'); /* --kp-bp-nav */

  class KpPdpReviews extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-kp-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-kp-panel]'));
      this.bars = Array.from(this.querySelectorAll('[data-kp-filter]'));
      this.histogram = this.querySelector('.kp-pdp-reviews__histogram');
      this.sort = this.querySelector('[data-kp-sort]');
      this.productSelect = this.querySelector('[data-kp-product-filter]');
      this.status = this.querySelector('[data-kp-status]');
      if (!this.panels.length) return;

      this.filter = null;
      this.product = '';
      /* The select is the source of truth for the starting sort: the PDP
       * opens on Judge.me's own order, the all-reviews page on date, since
       * its list is stitched together product by product and Judge.me's
       * order within each says nothing across them. */
      this.sortKey = (this.sort && this.sort.value) || 'recent';
      this.shown = new Map(); /* panel → number of cards currently paged in */

      this.querySelectorAll('[data-kp-needs-js]').forEach((el) => el.removeAttribute('hidden'));
      this.querySelectorAll('[data-kp-needs-nojs]').forEach((el) => el.setAttribute('hidden', ''));

      /* Which bars the server disabled — a rating nobody gave. The All tab
       * disables every bar on top of that, and must not re-enable those. */
      this.barDisabled = new Map(this.bars.map((bar) => [bar, bar.disabled]));

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      if (this.sort) this.sort.addEventListener('change', this.onSort);
      if (this.productSelect) this.productSelect.addEventListener('change', this.onProduct);
      NARROW.addEventListener('change', this.onNarrow);
      MID.addEventListener('change', this.onNarrow);

      /* The canonical order of each panel's cards. The DOM is re-packed
       * into columns, so DOM order stops meaning anything; this array is
       * what sort reorders and what paging reads. */
      this.order = new Map();
      this.panels.forEach((panel) => {
        this.order.set(panel, Array.from(panel.querySelectorAll('[data-kp-review]')));
      });

      if (this.sortKey !== 'recent') this.panels.forEach((panel) => this.reorder(panel));
      this.panels.forEach((panel) => this.page(panel, true));

      /* Both panels came down open. Close all but the selected tab's. */
      const selected = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
      if (selected) this.select(selected);
      else this.measureQuotes();
    }

    disconnectedCallback() {
      NARROW.removeEventListener('change', this.onNarrow);
      MID.removeEventListener('change', this.onNarrow);
    }

    get columns() {
      return NARROW.matches ? 1 : MID.matches ? 2 : 3;
    }

    /* ── Paging ──────────────────────────────────────────────────────── */

    initial(panel) {
      const key = NARROW.matches ? 'initialNarrow' : 'initial';
      return Number(panel.dataset[key] || this.dataset[key]) || 6;
    }

    step(panel) {
      const key = NARROW.matches ? 'stepNarrow' : 'step';
      return Number(panel.dataset[key] || this.dataset[key]) || 3;
    }

    /* The cards in a panel that pass both filters, in canonical order. */
    matching(panel) {
      return this.order.get(panel).filter(
        (item) =>
          (this.filter === null || item.dataset.kpRating === this.filter) &&
          (this.product === '' || item.dataset.kpProduct === this.product)
      );
    }

    /* Show the first `count` matching cards, hide the rest, and set the
     * foot to match. `reset` starts over at the initial page. */
    page(panel, reset) {
      const items = this.order.get(panel);
      const list = this.matching(panel);
      const count = reset ? this.initial(panel) : (this.shown.get(panel) || this.initial(panel));
      const visible = new Set(list.slice(0, count));

      items.forEach((item) => item.toggleAttribute('hidden', !visible.has(item)));
      this.shown.set(panel, count);
      this.pack(panel, list.slice(0, count));

      const more = panel.querySelector('.kp-pdp-reviews__more');
      if (more) more.closest('.kp-btn-frame').toggleAttribute('hidden', visible.size >= list.length);

      const empty = panel.querySelector('[data-kp-empty]');
      if (empty) empty.toggleAttribute('hidden', list.length > 0);

      if (!panel.hidden) this.announce(panel, visible.size, list.length);
    }

    announce(panel, visible, total) {
      if (!this.status) return;
      const template = this.dataset.status || '[visible] / [total]';
      this.status.textContent = template.replace('[visible]', visible).replace('[total]', total);
    }

    get activePanel() {
      return this.panels.find((panel) => !panel.hidden) || this.panels[0];
    }

    /* ── Events ──────────────────────────────────────────────────────── */

    onClick = (event) => {
      const tab = event.target.closest('[data-kp-tab]');
      if (tab) return this.select(tab);

      const bar = event.target.closest('[data-kp-filter]');
      if (bar && !bar.disabled) return this.toggleFilter(bar);

      const more = event.target.closest('.kp-pdp-reviews__more');
      if (more) {
        const panel = more.closest('[data-kp-panel]');
        const before = new Set(this.matching(panel).filter((item) => !item.hidden));
        this.shown.set(panel, (this.shown.get(panel) || this.initial(panel)) + this.step(panel));
        this.page(panel, false);
        /* Focus the first card that just appeared, so a keyboard user is not
         * left on a button that may have just vanished. */
        const first = this.matching(panel).find((item) => !item.hidden && !before.has(item));
        if (first) {
          first.setAttribute('tabindex', '-1');
          first.focus();
        }
        this.measureQuotes();
        return;
      }

      const toggle = event.target.closest('[data-kp-more]');
      if (toggle) return this.toggleQuote(toggle);
    };

    onKeydown = (event) => {
      const tab = event.target.closest('[data-kp-tab]');
      if (!tab) return;
      const tabs = this.tabs.filter((t) => !t.hidden);
      const i = tabs.indexOf(tab);
      let next = null;
      if (event.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
      else if (event.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
      else if (event.key === 'Home') next = tabs[0];
      else if (event.key === 'End') next = tabs[tabs.length - 1];
      if (!next) return;
      event.preventDefault();
      this.select(next);
      next.focus();
    };

    onProduct = () => {
      this.product = this.productSelect.value;
      this.panels.forEach((panel) => this.page(panel, true));
      this.measureQuotes();
    };

    onSort = () => {
      this.sortKey = this.sort.value;
      this.panels.forEach((panel) => {
        this.reorder(panel);
        this.page(panel, true);
      });
    };

    onNarrow = () => {
      this.panels.forEach((panel) => this.page(panel, true));
    };

    /* ── Tabs ────────────────────────────────────────────────────────── */

    select(tab) {
      this.tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
      });
      this.panels.forEach((panel) => {
        panel.toggleAttribute('hidden', panel.dataset.kpPanel !== tab.dataset.kpTab);
      });
      this.lockFilter();
      /* Re-pack on show, not just on page: a hidden panel measures every
       * column at 0, so packing it while hidden puts every card in the
       * first column. The tab that was just opened is laid out now. */
      const panel = this.activePanel;
      this.page(panel, false);
      this.measureQuotes();
    }

    /* ── Filter ──────────────────────────────────────────────────────── */

    /* The histogram belongs to the product. Off the product panel the rows
     * are display only: disabled, and whatever was filtered is released so
     * the list the visitor just opened is not silently cut down by a
     * control that no longer describes it. */
    lockFilter() {
      const locked = !('kpFilterable' in this.activePanel.dataset);
      this.bars.forEach((bar) => { bar.disabled = locked || this.barDisabled.get(bar); });
      if (this.histogram) this.histogram.toggleAttribute('data-kp-locked', locked);
      if (!locked || this.filter === null) return;
      this.filter = null;
      this.bars.forEach((bar) => bar.setAttribute('aria-pressed', 'false'));
      if (this.histogram) this.histogram.removeAttribute('data-kp-filtering');
      this.panels.forEach((panel) => this.page(panel, true));
    }

    toggleFilter(bar) {
      const rating = bar.dataset.kpFilter;
      this.filter = this.filter === rating ? null : rating;
      this.bars.forEach((b) => b.setAttribute('aria-pressed', b.dataset.kpFilter === this.filter ? 'true' : 'false'));
      if (this.histogram) this.histogram.toggleAttribute('data-kp-filtering', this.filter !== null);
      this.panels.forEach((panel) => this.page(panel, true));
    }

    /* ── Sort ────────────────────────────────────────────────────────── */

    reorder(panel) {
      const items = this.order.get(panel).slice();
      const key = this.sortKey;
      const byOrder = (a, b) => Number(a.dataset.kpOrder) - Number(b.dataset.kpOrder);
      const byDate = (a, b) => (a.dataset.kpDate < b.dataset.kpDate ? 1 : a.dataset.kpDate > b.dataset.kpDate ? -1 : byOrder(a, b));
      const byRating = (dir) => (a, b) => dir * (Number(b.dataset.kpRating) - Number(a.dataset.kpRating)) || byDate(a, b);
      const byPhoto = (a, b) => Number(b.dataset.kpPhoto) - Number(a.dataset.kpPhoto) || byOrder(a, b);

      const sorters = { recent: byOrder, date: byDate, highest: byRating(1), lowest: byRating(-1), photos: byPhoto };
      items.sort(sorters[key] || byOrder);
      this.order.set(panel, items);
    }

    /* ── Masonry ─────────────────────────────────────────────────────── */

    /* Pack the visible cards into column boxes, each card into whichever
     * column is currently shortest, in order. Hidden cards are parked in
     * the first column: they are display:none, so where they sit is moot,
     * but they must stay in the DOM for the next page or filter. */
    pack(panel, visible) {
      const grid = panel.querySelector('[data-kp-grid]');
      const n = this.columns;
      let cols = Array.from(grid.querySelectorAll(':scope > .kp-pdp-reviews__col'));
      if (cols.length !== n) {
        cols.forEach((col) => col.remove());
        cols = [];
        for (let i = 0; i < n; i += 1) {
          const col = document.createElement('div');
          col.className = 'kp-pdp-reviews__col';
          cols.push(col);
        }
      }
      grid.setAttribute('data-kp-packed', '');
      /* Detach everything first so the measurements below start from empty
       * columns rather than from whatever the last pass left. */
      this.order.get(panel).forEach((item) => item.remove());
      cols.forEach((col) => grid.appendChild(col));

      visible.forEach((item) => {
        let target = cols[0];
        if (n > 1) {
          cols.forEach((col) => { if (col.offsetHeight < target.offsetHeight) target = col; });
        }
        target.appendChild(item);
      });
      this.order.get(panel).forEach((item) => { if (!item.parentNode) cols[0].appendChild(item); });
    }

    /* ── Read more ───────────────────────────────────────────────────── */

    measureQuotes() {
      const run = () => {
        this.querySelectorAll('[data-kp-more]').forEach((toggle) => {
          const quote = document.getElementById(toggle.getAttribute('aria-controls'));
          if (!quote || quote.classList.contains('is-open')) return;
          /* An unclamped quote never overflows; a hidden one measures 0.
           * Both leave the toggle hidden, and the hidden card is measured
           * again the next time its panel is shown. */
          const overflows = quote.scrollHeight > quote.clientHeight + 1;
          toggle.toggleAttribute('hidden', !overflows);
        });
      };
      if (document.fonts && document.fonts.status !== 'loaded') {
        document.fonts.ready.then(run);
      } else {
        run();
      }
    }

    toggleQuote(toggle) {
      const quote = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!quote) return;
      const open = !quote.classList.contains('is-open');
      quote.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      toggle.textContent = open ? toggle.dataset.labelLess : toggle.dataset.labelMore;
    }
  }

  customElements.define('kp-pdp-reviews', KpPdpReviews);
})();
